<template>
  <div class="page-container">
    <div class="box">
      <span class="md-title">Zones atypiques</span>
      <md-table
        v-model="afterWorkAtypical">
        <md-table-empty-state
          md-label="Aucun lieu atypique"
          md-description="Aucun lieu atypique n'est renseigné pour votre parc bati" />
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item }">
          <md-table-cell
            md-label="Zone">
            <button
              class="md-button md-dense md-theme-default table-edit-button"
              type="button"
              @click="fillAfterWorkAtypicalForms(item)">
              <div class="md-button-content">
                {{ item.place_details.name }} ({{ item.place_details.identifier }})
                <md-icon>edit</md-icon>
              </div>
            </button>
          </md-table-cell>
          <md-table-cell
            md-label="Coût énergie">
            {{ item.energy_investment_display|valueIfNotNone }}
          </md-table-cell>
          <md-table-cell
            md-label="Coût global">
            {{ item.total_investment_display|valueIfNotNone }}
          </md-table-cell>
          <md-table-cell
            md-label="Économie estimée*"
            md-tooltip="L'économie est calculée à coût constant pour l'énergie, elle est donc à priori sous-évaluée">
            {{ item.economy_display|valueIfNotNone }}
          </md-table-cell>
          <md-table-cell
            md-label="Temps de retour estimé**"
            md-tooltip="Le temps de retour calculé est à priori sur-évalué">
            {{ item.return_time_display|valueIfNotNone }}
          </md-table-cell>
          <md-table-cell
            md-label="Année">
            <strong>{{ item.year|valueIfNotNone }}</strong>
          </md-table-cell>
        </md-table-row>
      </md-table>
    </div>

    <md-dialog :md-active.sync="afterWorkAtypicalForms.showAtypicalDialog">
      <md-dialog-title>Paramétrage de l'action atypique</md-dialog-title>

      <md-dialog-content>
        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <md-field>
              <label>Zone</label>
              <md-input
                :value="afterWorkAtypicalForms.area.displayName"
                type="text"
                name="zone"
                disabled />
            </md-field>
          </div>
          <div
            class="md-layout-item"
            v-if="afterWorkAtypicalForms.area.comment">
            <md-field>
              <label>Commentaire zone</label>
              <md-input
                :value="afterWorkAtypicalForms.area.comment"
                type="text"
                name="comment"
                disabled />
            </md-field>
          </div>
          <div class="md-layout-item">
            <md-field
              :class="{ 'md-invalid': $v.afterWorkAtypicalForms.year.$error }">
              <label>Année de réalisation de l'action</label>
              <md-select
                v-model="afterWorkAtypicalForms.year"
                name="year"
                required>
                <md-option
                  v-for="year in years"
                  :key='year'
                  :value="year">{{ year }}</md-option>
              </md-select>
              <span
                class="md-error"
                v-if="!$v.afterWorkAtypicalForms.year.required">Ce champ est requis</span>
            </md-field>
          </div>
        </div>

        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <md-card>
              <md-card-header>
                <h2>Coûts estimés de réalisation de l'action</h2>
              </md-card-header>
              <md-card-content>
                <md-field
                  :class="{ 'md-invalid': $v.afterWorkAtypicalForms.energy_investment.$error }">
                  <label for="energy_investment">Coûts des travaux liés à l'énergie</label>
                  <md-input
                    v-model.trim="$v.afterWorkAtypicalForms.energy_investment.$model"
                    type="number"
                    name="energy_investment"
                    required />
                  <span class="md-suffix">€/an</span>
                  <span
                    class="md-error"
                    v-if="!$v.afterWorkAtypicalForms.energy_investment.required">Ce champ est requis</span>
                  <span
                    class="md-error"
                    v-if="!$v.afterWorkAtypicalForms.energy_investment.minValue">Saisissez une valeur positive</span>
                </md-field>
                <md-field
                  :class="{ 'md-invalid': $v.afterWorkAtypicalForms.total_investment.$error }">
                  <label for="total_investment">Coût total de réalisation de l'action</label>
                  <md-input
                    v-model.trim="$v.afterWorkAtypicalForms.total_investment.$model"
                    type="number"
                    name="total_investment"
                    required />
                  <span class="md-suffix">€</span>
                  <span
                    class="md-error"
                    v-if="!$v.afterWorkAtypicalForms.total_investment.required">Ce champ est requis</span>
                  <span
                    class="md-error"
                    v-if="!$v.afterWorkAtypicalForms.total_investment.minValue">Saisissez une valeur positive</span>
                </md-field>
              </md-card-content>
            </md-card>
          </div>
        </div>

        <div class="md-title">Consommations estimées après réalisation de l'action</div>

        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <md-card
              v-for='energy in $v.afterWorkAtypicalForms.energies.$each.$iter'
              :key="`energy-${energy.$model.id}`">
              <md-card-header>
                <h2>{{ energy.$model.label }}</h2>
              </md-card-header>

              <md-card-content>
                <md-field>
                  <label>Consommation année {{ afterWorkAtypicalForms.consumptions_year }}</label>
                  <md-input
                    :value="energy.$model.total_energy_consumption | formatIntNumber"
                    type="text"
                    name="energy"
                    disabled />
                  <span class="md-suffix">
                    kWh<acronym title="Énergie Finale">EF</acronym>
                  </span>
                </md-field>

                <md-field :class="{ 'md-invalid': energy.after_work_energy_consumption.$error }">
                  <label for="first-name">Consommation annuelle après travaux</label>
                  <md-input
                    v-model.trim="energy.after_work_energy_consumption.$model"
                    type="number"
                    name="after_work_energy_consumption" />
                  <span class="md-suffix">kWh<acronym title="Énergie Finale">EF</acronym>/an</span>
                  <span
                    class="md-error"
                    v-if="!energy.after_work_energy_consumption.minValue">Saisissez une valeur positive</span>
                  <span
                    class="md-error"
                    v-if="!energy.after_work_energy_consumption.required">Ce champ est requis</span>
                </md-field>
              </md-card-content>
            </md-card>
          </div>
        </div>

        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <div class="md-title">Commentaires</div>
            <md-field>
              <label>Commentaires</label>
              <md-textarea v-model="afterWorkAtypicalForms.comment"/>
              <md-icon>description</md-icon>
            </md-field>
          </div>
        </div>
        <md-snackbar
          :md-position="'center'"
          :md-active.sync="afterWorkAtypicalForms.showSnackbar">
          <span>Veuillez corriger les erreurs puis ré-essayer</span>
        </md-snackbar>
      </md-dialog-content>

      <md-dialog-actions class="dialog-actions">
        <div class="md-layout-item dialog-action-left">
          <md-button
            class="md-accent"
            v-if='afterWorkAtypicalForms.id'
            @click="deleteAfterWorkAtypical()">Supprimer</md-button>
        </div>
        <div class="md-layout-item dialog-action-right">
          <md-button
            @click="cancelAfterWorkAtypicalForms()">Annuler</md-button>
          <md-button
            class="md-primary"
            @click="cleanAfterWorkAtypicalForm()">Valider</md-button>
        </div>
      </md-dialog-actions>
    </md-dialog>

  </div>
</template>

<script>
import session from '../api/session'
import axios from 'axios'
import { validationMixin } from 'vuelidate'
import ValuesDisplayMixin from '../mixins/ValuesDisplayMixin'
import { required, minValue } from 'vuelidate/lib/validators'

export default {
  name: 'AtypicalList',
  mixins: [validationMixin, ValuesDisplayMixin],
  data () {
    return {
      energyList: [],
      afterWorkAtypical: [],
      userPlaces: [],
      years: [],
      afterWorkAtypicalForms: {
        id: null,
        area: {},
        year: null,
        comment: null,
        energy_investment: null,
        total_investment: null,
        consumptions_year: null,
        energies: [],
        showAtypicalDialog: false,
        showSnackbar: false
      }
    }
  },
  mounted: function () {
    this.$store.dispatch('routing/update_title', 'Actions sur lieux atypiques')
    this.$store.dispatch('routing/update_return_kwargs', null)
    this.loadApi()
  },
  validations () {
    var validations = {
      afterWorkAtypicalForms: {
        year: {
          required
        },
        energy_investment: {
          required,
          minValue: minValue(0)
        },
        total_investment: {
          required,
          minValue: minValue(0)
        },
        energies: {
          $each: {
            after_work_energy_consumption: {
              required,
              minValue: minValue(0)
            }
          }
        }
      }
    }
    return validations
  },
  methods: {
    fillAfterWorkAtypicalForms (afterWorkPPI) {
      var app = this
      var area = app.getUserPlace(afterWorkPPI.place)

      app.afterWorkAtypicalForms.area = area
      app.afterWorkAtypicalForms.energies = []

      if (afterWorkPPI.id) {
        app.afterWorkAtypicalForms.id = afterWorkPPI.id
        app.afterWorkAtypicalForms.year = afterWorkPPI.year
        app.afterWorkAtypicalForms.energy_investment = parseFloat(afterWorkPPI.energy_investment)
        app.afterWorkAtypicalForms.total_investment = parseFloat(afterWorkPPI.total_investment)
        app.afterWorkAtypicalForms.comment = afterWorkPPI.comment
      }

      // Retrieve consumptions for selected areas
      session.get('/api/userdata/place-energy-consumptions/', {params: {'places': [area.id]}}).then(function (response) {
        var areaConsumptions = response.data
        app.afterWorkAtypicalForms.consumptions_year = areaConsumptions[0].year

        areaConsumptions.forEach(function (areaConsumption) {
          var energy = app.getEnergy(areaConsumption.energy)

          app.$set(energy, 'total_energy_consumption', areaConsumption.total_energy_consumption)
          app.$set(energy, 'after_work_energy_consumption', null)

          if (afterWorkPPI.id) {
            var afterWorkEnergyConsumption = afterWorkPPI.after_work_energies.filter(afterWorkEnergy => afterWorkEnergy.energy === energy.id)

            if (afterWorkEnergyConsumption.length > 0) {
              app.$set(energy, 'after_work_energy_consumption', parseFloat(afterWorkEnergyConsumption[0].energy_consumption))
            }
          }
          app.afterWorkAtypicalForms.energies.push(energy)
          app.$v.afterWorkAtypicalForms.$reset()
        })
        app.afterWorkAtypicalForms.showAtypicalDialog = true
      }).catch(function (error) {
        console.log(error)
        app.afterWorkAtypicalForms.showAtypicalDialog = false
      })
    },
    cleanAfterWorkAtypicalForm () {
      var app = this
      app.$v.afterWorkAtypicalForms.$touch()

      if (!app.$v.afterWorkAtypicalForms.$invalid) {
        app.postAfterWorkAtypicalForms()
        app.afterWorkAtypicalForms.showSnackbar = false
      } else {
        app.afterWorkAtypicalForms.showSnackbar = true
      }
    },
    postAfterWorkAtypicalForms: function () {
      var app = this
      var year = app.afterWorkAtypicalForms.year

      var afterWorkAtypical = {
        'place': app.afterWorkAtypicalForms.area.id,
        'year': year,
        'comment': app.afterWorkAtypicalForms.comment,
        'total_investment': app.afterWorkAtypicalForms.total_investment,
        'energy_investment': app.afterWorkAtypicalForms.energy_investment,
        'after_work_energies': []
      }

      app.afterWorkAtypicalForms.energies.forEach(function (energy) {
        afterWorkAtypical['after_work_energies'].push({
          'energy': energy.id,
          'energy_consumption': energy.after_work_energy_consumption})
      })

      var promeseResult = null

      app.$store.dispatch('loading/begin_loading')

      if (app.afterWorkAtypicalForms.id) {
        var atypicalId = app.afterWorkAtypicalForms.id
        promeseResult = session.put(`/api/userdata/atypical/${atypicalId}/`, afterWorkAtypical)
      } else {
        promeseResult = session.post(`/api/userdata/atypical/`, afterWorkAtypical)
      }

      promeseResult.then(function (response) {
        var afterWorkAtypicalResponse = response.data
        var afterWorkAtypicalIndex = app.afterWorkAtypical.findIndex(function (s) { return s.place === afterWorkAtypicalResponse.place })
        app.$set(app.afterWorkAtypical, afterWorkAtypicalIndex, afterWorkAtypicalResponse)
        app.resetAfterWorkAtypicalForms()
      }).catch(function (error) {
        console.log(error)
        app.afterWorkAtypicalForms.showAtypicalDialog = false
      }).then(function (response) {
        app.$store.dispatch('loading/end_loading')
      })
    },
    resetAfterWorkAtypicalForms: function () {
      var app = this
      app.afterWorkAtypicalForms.id = null
      app.afterWorkAtypicalForms.area = {}
      app.afterWorkAtypicalForms.year = null
      app.afterWorkAtypicalForms.comment = null
      app.afterWorkAtypicalForms.energy_investment = null
      app.afterWorkAtypicalForms.total_investment = null
      app.afterWorkAtypicalForms.consumptions_year = null
      app.afterWorkAtypicalForms.energies = []
      app.afterWorkAtypicalForms.showAtypicalDialog = false
      app.afterWorkAtypicalForms.showSnackbar = false
      app.$v.afterWorkAtypicalForms.$reset()
    },
    cancelAfterWorkAtypicalForms: function () {
      var app = this
      app.resetAfterWorkAtypicalForms()
    },
    deleteAfterWorkAtypical: function () {
      var app = this

      app.$store.dispatch('loading/begin_loading')
      if (app.afterWorkAtypicalForms.id) {
        var atypicalId = app.afterWorkAtypicalForms.id
        session.delete(`/api/userdata/atypical/${atypicalId}/`).then(function (response) {
          var afterWorkAtypical = app.afterWorkAtypical.filter(atypical => atypical.id === atypicalId)[0]
          var afterWorkAtypicalIndex = app.afterWorkAtypical.findIndex(function (s) { return s.id === response.data.id })

          afterWorkAtypical.year = null
          afterWorkAtypical.after_work_energies = []
          afterWorkAtypical.comment = null
          afterWorkAtypical.economy_display = null
          afterWorkAtypical.energy_investment = null
          afterWorkAtypical.energy_investment_display = null
          afterWorkAtypical.id = null
          afterWorkAtypical.return_time_display = null
          afterWorkAtypical.total_investment = null
          afterWorkAtypical.total_investment_display = null

          app.$set(app.afterWorkAtypical, afterWorkAtypicalIndex, afterWorkAtypical)
          app.resetAfterWorkAtypicalForms()
        }).catch(function (error) {
          app.afterWorkAtypicalForms.showAtypicalDialog = false
          console.log(error)
        }).then(function (response) {
          app.$store.dispatch('loading/end_loading')
        })
      }
    },
    loadApi: function () {
      var app = this

      function getPlaces (app) {
        var params = {type: 'area', atypical: true}
        return session.get('/api/userdata/places/', {params: params}).catch(function (error) { console.log(error) })
      }

      function getAfterWorkAtypical (app) {
        return session.get(`/api/userdata/atypical/`).catch(function (error) { console.log(error) })
      }

      function getEnergyList (app) {
        return session.get(`/api/parameters/energies/`).catch(function (error) { console.log(error) })
      }

      app.$store.dispatch('loading/begin_loading')

      axios.all([getPlaces(app), getAfterWorkAtypical(app), getEnergyList(app)])
        .then(axios.spread(function (placesResponse, afterWorkAtypicalResponse, energyListResponse) {
          var minYear = 2010
          var maxYear = 2050
          var nbYears = maxYear - minYear + 1

          app.afterWorkAtypical = afterWorkAtypicalResponse.data
          app.energyList = energyListResponse.data
          app.userPlaces = []

          placesResponse.data.forEach(function (place) {
            place.displayName = app.placeChoiceName(place)
            app.userPlaces.push(place)
          })

          app.years = Array.from(new Array(nbYears), (val, index) => index + minYear)

          app.$store.dispatch('loading/end_loading')
        }))
    },
    getUserPlace: function (placeId) {
      return this.userPlaces.filter(place => place.id === placeId)[0]
    },
    getEnergy: function (energyId) {
      return this.energyList.filter(energy => energy.id === energyId)[0]
    }
  }
}
</script>

<style lang="scss" scoped>
.box {
  margin: 2rem;
}

.table-edit-button {
  margin: 0;
}

.md-card {
  display: inline-block;
  min-width: 400px;
  max-width: 500px;
  margin: 1em 3em 1em 0;
}
</style>



// WEBPACK FOOTER //
// src/views/AtypicalList.vue